import React, { memo, type ComponentType } from 'react';
import { useFragment, graphql } from 'react-relay';
import { MenuSection, MenuSectionHeading } from '@atlaskit/side-nav-items/menu-section';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import LinkMenuItem from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/link-menu-item-with-analytics/index.tsx';
import { useCurrentRoute } from '@jira/platform__utils-router/src/index.tsx';
import type { DelegatedAdminJiraSection$key } from './__generated__/DelegatedAdminJiraSection.graphql';
import { COMMON_ANALYTICS_ATTRIBUTES } from './constants.tsx';

const heading = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.jira-section.heading',
	defaultMessage: 'Jira',
	description: 'Navigation group heading for Jira settings items',
});

const onboardingHubItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.jira-section.onboarding-hub-item',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Custom onboarding',
	description: 'Navigation item which links to the Onboarding Hub page',
});

type Props = {
	queryReference?: DelegatedAdminJiraSection$key;
};

const DelegatedAdminJiraSectionInternal = ({ queryReference }: Props) => {
	const { formatMessage } = useIntl();
	const currentRoute = useCurrentRoute();

	// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
	const fragmentData = useFragment<DelegatedAdminJiraSection$key>(
		graphql`
			fragment DelegatedAdminJiraSection on Query
			@argumentDefinitions(
				cloudId: { type: "ID!" }
				skipDelegatedAdminCheck: { type: "Boolean!" }
			) {
				jira @skip(if: $skipDelegatedAdminCheck) {
					canManageCustomOnboarding: hasGlobalPermission(
						cloudId: $cloudId
						key: MANAGE_CUSTOM_ONBOARDING
					) @optIn(to: "JiraHasGlobalPermission")
				}
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		queryReference,
	);

	if (!fragmentData?.jira?.canManageCustomOnboarding) {
		return null;
	}

	return (
		<MenuSection isMenuListItem>
			<MenuSectionHeading>{formatMessage(heading)}</MenuSectionHeading>
			<MenuList>
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'onboardingHub' }}
					isSelected={[
						'products-onboarding-hub',
						'products-onboarding-hub-create',
						'products-onboarding-hub-edit',
					].includes(currentRoute.name)}
					href="/jira/settings/products/onboarding"
				>
					{formatMessage(onboardingHubItem)}
				</LinkMenuItem>
			</MenuList>
		</MenuSection>
	);
};

export const DelegatedAdminJiraSection: ComponentType<Props> = memo(
	DelegatedAdminJiraSectionInternal,
);
